import React from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';

interface PromptTemplate {
  service: string;
  templates: string[];
}

interface PromptTemplatesModalProps {
  show: boolean;
  handleClose: () => void;
  copyToMessageBox: (template: string) => void;
}

const promptTemplates: PromptTemplate[] = [
  {
    service: 'Blog Writing',
    templates: [
      'Write a compelling blog post on [topic] that appeals to [audience]. Focus on [tone/style] and include actionable insights and a call to action.',
      'Create a blog post about [industry/product] for [target audience]. Highlight [key points] and maintain a [tone, e.g., friendly, expert]. Add practical tips and a closing call to action.',
    ],
  },
  {
    service: 'Social Media Content Writing',
    templates: [
      'Generate a social media post for [platform] about [topic/product]. Keep it [tone: engaging, humorous], and include relevant hashtags and a clear call to action.',
      'Create a series of social posts for [campaign or topic]. Focus on [tone] and add hashtags, engaging visuals, and an actionable message.',
    ],
  },
  {
    service: 'Email Campaign',
    templates: [
      'Draft an email campaign for [event or product] targeting [audience segment]. Highlight benefits, keep it [tone], and include a compelling call to action.',
      'Create a personalized email for [promotion/announcement] aimed at [target audience]. Emphasize [main benefits] and end with an inviting call to action for [desired response].',
    ],
  },
  {
    service: 'Brand & Identity Creation (Beta)',
    templates: [
      'Create a unique brand identity for a [type of business], focusing on [specific qualities, e.g., trustworthiness, innovation]. Include guidelines for colors, logo, and tagline.',
      'Develop a brand identity that resonates with [target audience] for a [type of business]. Highlight [qualities, e.g., eco-friendliness, approachability].',
    ],
  },
  {
    service: 'Graphic Designing (Beta)',
    templates: [
      'Design a [type of graphic, e.g., social media post, flyer] for [specific purpose] with a [desired style, e.g., minimalist, vibrant] theme.',
      'Create a [graphic type] that visually represents [theme or message]. Use [style guidelines, e.g., bold colors, modern typography].',
    ],
  },
  {
    service: 'SEO Recommendations',
    templates: [
      'Provide SEO recommendations for a website focused on [industry, e.g., e-commerce, real estate] to improve [target metrics, e.g., organic reach, keyword ranking].',
      'Suggest SEO improvements for [type of website] to increase visibility. Focus on [specific area, e.g., content, page speed].',
    ],
  },
  {
    service: 'Image Enhancement',
    templates: [
      'Enhance an image by [action, e.g., upscaling, removing background] for [purpose, e.g., social media, presentation].',
      'Modify an image by [specific action, e.g., background removal, creative generation] to fit [intended use, e.g., marketing, portfolio].',
    ],
  },
  {
    service: 'Presentation Creation',
    templates: [
      'Create a PowerPoint on [topic, e.g., quarterly results, product launch] with a structured outline.',
      'Generate a complete presentation file on [subject, e.g., marketing strategies] with professional slides and flow.',
    ],
  },
  {
    service: 'Web Page Builder (Beta)',
    templates: [
      'Create a single-page website on [topic, e.g., portfolio, event] with sections for [details, e.g., about, contact].',
      'Design a web page focused on [purpose, e.g., product launch, blog] with visual elements and integrated graphs.',
    ],
  },
  // {
  //   service: 'Lead Enrichment Tool',
  //   templates: [
  //     'Enrich lead profile by gathering details from [source, e.g., LinkedIn, Google] for [industry].',
  //     'Enhance lead details by adding info from [sources, e.g., Google] for better targeting.',
  //   ],
  // },
  {
    service: 'Domain Finder',
    templates: [
      'Check domain availability for [business name, niche] and suggest similar available options.',
      '"Find an available domain name for [purpose, e.g., e-commerce, blog] that reflects brand identity.',
    ],
  },
  // {
  //   service: 'Knowledge Base Manager',
  //   templates: [
  //     'Create a knowledge set on [topic, e.g., product FAQs] to streamline information retrieval.',
  //     'Update existing knowledge set with new entries on [subject, e.g., industry insights].',
  //   ],
  // },
  {
    service: 'Data Categorization Tool',
    templates: [
      'Categorize data into [specified categories] for easier analysis and tracking.',
      'Organize incoming data by [category, e.g., region, purchase type] to aid reporting.',
    ],
  },
  {
    service: 'Copy Enhancement Suite',
    templates: [
      'Enhance copy with [framework, e.g., AIDA] for targeted messaging and increased engagement.',
      'Optimize text using [tools, e.g., translator, PR inspection] to refine message clarity.',
    ],
  },
];

const PromptTemplatesModal: React.FC<PromptTemplatesModalProps> = ({ show, handleClose, copyToMessageBox }) => {
  const copyToClipboard = (template: string) => {
    navigator.clipboard.writeText(template).then(() => {
      alert('Copied to clipboard!');
    });
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="prompt-templates-modal">
    <Modal.Header closeButton>
      <Modal.Title>Prompt Templates</Modal.Title>
    </Modal.Header>
    <Modal.Body>
       
        {promptTemplates.map((service, index) => (
          <div key={index} className="mb-4">
            <h5>{service.service}</h5>
            <ListGroup>
              {service.templates.map((template, templateIndex) => (
             <ListGroup.Item key={templateIndex}>
             <span>{template}</span>
             <div className="btn-group">
               <Button
                 variant="outline-primary"
                 size="sm"
                 onClick={() => copyToMessageBox(template)}
               >
                 Copy to Message Box
               </Button>
               <Button
                 variant="outline-secondary"
                 size="sm"
                 onClick={() => copyToClipboard(template)}
               >
                 Copy to Clipboard
               </Button>
             </div>
           </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        ))}
      </Modal.Body>
    </Modal>
  );
};

export default PromptTemplatesModal;